import React from 'react'
import styled from 'styled-components'
import { inject, observer } from 'mobx-react'
import { StyledCard } from '../Cards/StyledCard'
import { Button, Col, Form } from 'react-bootstrap'
import { withTranslation } from 'react-i18next'
import ArraySelect from '../Select/ArraySelect'
import ZipSuggestion from '../Forms/ZipSuggestion'
import { getFakeEvent } from '../../utils/utils'
import { useLocation } from 'react-router-dom'
import { useState } from 'react'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'

const StyledAddressCard = styled.div`
  width: 100%;
  padding: 10px;

  @media screen and (min-width: 768px) {
    width: 50%;
    display: flex;

    > div {
      flex-grow: 1;
    }
  }
`

const StyledCustomAddressCard = styled(StyledAddressCard)`
  @media screen and (min-width: 768px) {
    width: 100%;
  }
`

const StyledTitle = styled.span`
  letter-spacing: 0.8px;
`

const StyledCWSForm = styled.div`
  input {
    outline: none;
    border: none;
    border-bottom: 1px solid black;
    border-radius: 0;
  }
  select {
    outline: none;
    border: none;
    border-bottom: 1px solid black;
    border-radius: 0;
  }
`

const CustomShipmentAddress = inject(
  'productsStore',
  'checkoutStore'
)(
  observer((props) => {
    const [isParseAddressFieldOpen, setIsParseAddressFieldOpen] =
      useState(false)
    const { t, productsStore, checkoutStore } = props
    const mandate = process.env.REACT_APP_MANDATORSET
    const location = useLocation()
    const isPrivateOrder = location.pathname.includes('/private')
    if (!checkoutStore.isCustomShipmentAddressAvailable()) {
      return null
    }

    const handleCustomShipmentAddressCardClick = () => {
      productsStore.setCustomShipmentAddressSelected(true)
    }

    const [availableCountries, setAvailableCountries] = useState([])

    const fetchAvailableCountries = async () => {
      const countries = await apiClient.getJson(APIEndpoints.countries)
      if (countries && countries.length) {
        setAvailableCountries(countries)
      }
    }

    const renderShipmentAddressForm = () => {
      const { customShipmentAddress, customAddressSettings } = productsStore
      const isSecuritasPrivate = mandate === 'securitas' && isPrivateOrder

      const handleAddressFieldToggle = () => {
        if (!isParseAddressFieldOpen) {
          const fieldsToClear = [
            'parsedAddressText',
            'officeName',
            'careOf',
            'firstName',
            'lastName',
            'street',
            'streetNr',
            'zipCode',
            'city',
            'countryIsoCode',
            'updateUserAddress',
          ]

          fieldsToClear.forEach((field) => {
            productsStore.handleChangeCustomShipmentAddress(
              getFakeEvent(
                field,
                field === 'updateUserAddress' ? false : '',
                field === 'updateUserAddress' ? 'checkbox' : 'text'
              )
            )
          })

          setIsParseAddressFieldOpen(true)
          fetchAvailableCountries()
        } else {
          setIsParseAddressFieldOpen(false)
        }
      }
      const parseAddress = (raw, availableCountries = []) => {
        const result = {
          officeName: '',
          careOf: '',
          firstName: '',
          lastName: '',
          street: '',
          streetNr: '',
          zipCode: '',
          city: '',
          countryIsoCode: '',
        }

        if (!raw?.trim()) return result

        const lines = raw
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean)

        const countryNameToIsoCode = new Map(
          availableCountries.map((country) => [
            country.displayName,
            country.isoCode,
          ])
        )

        const countryPatterns = [
          { iso: 'FR', pattern: /\b(france|frankreich|frankrijk|francia)\b/i },
          { iso: 'BE', pattern: /\b(belgium|belgië|belgien|belgique|be)\b/i },
          {
            iso: 'DE',
            pattern: /\b(germany|deutschland|allemagne|duitsland)\b/i,
          },
          {
            iso: 'HU',
            pattern: /\b(hungary|ungarn|hongarije|hongrie|magyarország)\b/i,
          },
          { iso: 'IT', pattern: /\b(italy|italien|italia|italië)\b/i },
          {
            iso: 'NL',
            pattern:
              /\b(netherlands|niederlande|pays-bas|holanda|nederland|holland)\b/i,
          },
          { iso: 'PL', pattern: /\b(poland|polen|pologne|polonia|polska)\b/i },
          { iso: 'PT', pattern: /\b(portugal)\b/i },
          { iso: 'ES', pattern: /\b(spain|spanien|espagne|spanje|españa)\b/i },
          {
            iso: 'SE',
            pattern: /\b(sweden|schweden|suède|zweeden|sverige)\b/i,
          },
          {
            iso: 'CH',
            pattern: /\b(switzerland|schweiz|suisse|svizzera|zwitserland)\b/i,
          },
          { iso: 'TR', pattern: /\b(turkey|türkei|turquie|türkiye)\b/i },
          {
            iso: 'GB',
            pattern:
              /\b(united kingdom|great britain|england|uk|gb|royaume-uni|reino unido|verenigd koninkrijk|großbritannien)\b/i,
          },
        ]

        for (const line of lines) {
          for (const { iso, pattern } of countryPatterns) {
            if (pattern.test(line)) {
              result.countryIsoCode = iso
              break
            }
          }
          if (result.countryIsoCode) break
        }

        if (!result.countryIsoCode) {
          for (const line of lines) {
            for (const [countryName, isoCode] of countryNameToIsoCode) {
              if (line.toLowerCase().includes(countryName.toLowerCase())) {
                result.countryIsoCode = isoCode
                break
              }
            }
            if (result.countryIsoCode) break
          }
        }

        if (!result.countryIsoCode) {
          for (const line of lines) {
            const isoCodeMatch = line.match(
              /\b(DE|FR|BE|HU|IT|NL|PL|PT|ES|SE|CH|TR|GB)\b/i
            )
            if (isoCodeMatch) {
              result.countryIsoCode = isoCodeMatch[0].toUpperCase()
              break
            }
          }
        }

        const zipCity = lines.find((l) => /^(?:[A-Z]{2}-)?\d{4,5}\s+/.test(l))
        if (zipCity) {
          const m = zipCity.match(/^(?:[A-Z]{2}-)?(\d{4,5})\s+(.+)$/)
          if (m) {
            result.zipCode = m[1]
            result.city = m[2].replace(/[,;].*$/, '').trim()
          }
        }

        const streetLine = lines.find((l) =>
          /(straße|strasse|weg|allee|platz|gasse|rue|via|route)/i.test(l)
        )

        if (streetLine) {
          const m1 = streetLine.match(/^(.+?)\s+(\d[\d\w-/]*)$/)
          const m2 = streetLine.match(/^(\d[\d\w-/]*)\s+(.+)$/)
          if (m1) {
            result.street = m1[1]
            result.streetNr = m1[2]
          } else if (m2) {
            result.streetNr = m2[1]
            result.street = m2[2]
          } else {
            result.street = streetLine
          }
        }

        const careOfLine = lines.find((l) =>
          /^(c\/o|z\.?h\.?|zu\s*hd\.?|attn|à l'attention)/i.test(l)
        )
        if (careOfLine) {
          result.careOf = careOfLine.replace(
            /^(c\/o|z\.?h\.?|zu\s*hd\.?|attn|à l'attention)[:-]?\s*/i,
            ''
          )
        }

        let companyLine = null

        companyLine = lines.find((l) =>
          /(gmbh|ag|kg|sarl|sàrl|sa|spa|inc|corp|company|ltd|co\.|kg|e\.v\.)/i.test(
            l
          )
        )

        if (!companyLine) {
          companyLine = lines.find((l) =>
            /(polizei|stadt|amt|behörde|verwaltung|ministerium|bundes|universität|krankenhaus|klinik|schule|institut|zentrum|verein|stiftung)/i.test(
              l
            )
          )
        }

        if (!companyLine && lines.length > 0) {
          const firstLine = lines[0]
          if (
            !/^[A-ZÄÖÜ][a-zäöüß]+\s+[A-ZÄÖÜ][a-zäöüß]+/.test(firstLine) &&
            !/^(c\/o|z\.?h\.?|zu\s*hd\.?|attn|à l'attention)/i.test(
              firstLine
            ) &&
            !/(straße|strasse|weg|allee|platz|gasse|rue|via|route)/i.test(
              firstLine
            )
          ) {
            companyLine = firstLine
          }
        }

        if (companyLine) {
          result.officeName = companyLine
        }

        const usedIndexes = []
        if (result.officeName) {
          const officeIndex = lines.findIndex((l) => l === result.officeName)
          if (officeIndex !== -1) usedIndexes.push(officeIndex)
        }
        if (result.careOf) {
          const careOfIndex = lines.findIndex((l) => l.includes(result.careOf))
          if (careOfIndex !== -1) usedIndexes.push(careOfIndex)
        }

        const nameLineIndex = lines.findIndex(
          (line, idx) =>
            !usedIndexes.includes(idx) &&
            /^[A-ZÄÖÜ][a-zäöüß]+\s+[A-ZÄÖÜ][a-zäöüß]+/.test(line)
        )

        if (nameLineIndex !== -1) {
          const nameParts = lines[nameLineIndex].split(' ')
          result.firstName = nameParts[0]
          result.lastName = nameParts.slice(1).join(' ')
        }

        return result
      }

      return (
        <Form name='customShipmentAddress' className='px-2'>
          <Form.Group className='mb-3'>
            {!isParseAddressFieldOpen && (
              <div className='d-flex justify-content-end'>
                <Button
                  size='sm'
                  variant={'primary'}
                  className='me-2'
                  onClick={handleAddressFieldToggle}
                >
                  {t('formularFields:addressfield')}
                </Button>
              </div>
            )}
            {isParseAddressFieldOpen && (
              <React.Fragment>
                <Form.Label>{t('formularFields:addressfield')}</Form.Label>
                <Form.Control
                  as='textarea'
                  placeholder={t('formularFields:parseFieldPlaceholder')}
                  value={customShipmentAddress.parsedAddressText || ''}
                  name='parsedAddressText'
                  onChange={(e) => {
                    const { value } = e.target
                    productsStore.handleChangeCustomShipmentAddress(
                      getFakeEvent('parsedAddressText', value, 'text')
                    )
                  }}
                  rows={5}
                />

                <div className='d-flex justify-content-end'>
                  <Button
                    type='button'
                    size='sm'
                    variant='primary'
                    className='mt-3 me-1'
                    disabled={
                      customShipmentAddress.parsedAddressText.length < 7
                    }
                    onClick={() => {
                      const raw = customShipmentAddress.parsedAddressText
                      const parsed = parseAddress(raw, availableCountries)
                      const fields = [
                        'officeName',
                        'careOf',
                        'firstName',
                        'lastName',
                        'street',
                        'streetNr',
                        'zipCode',
                        'city',
                        'countryIsoCode',
                      ]

                      fields.forEach((field) => {
                        if (parsed[field]) {
                          if (field === 'countryIsoCode') {
                            const match = checkoutStore.shippingCountries.find(
                              (c) =>
                                c.isoCode.toUpperCase() ===
                                parsed[field].toUpperCase()
                            )
                            if (match) {
                              productsStore.handleChangeCustomShipmentAddress({
                                target: {
                                  name: 'countryIsoCode',
                                  value: match.isoCode,
                                  type: 'select-one',
                                },
                              })
                            } else {
                              console.warn(
                                'No matching country found for:',
                                parsed[field]
                              )
                            }
                          } else {
                            productsStore.handleChangeCustomShipmentAddress(
                              getFakeEvent(field, parsed[field], 'text')
                            )
                          }
                        }
                      })
                      setIsParseAddressFieldOpen(false)
                    }}
                  >
                    {t('formularFields:proceedBtn')}
                  </Button>
                </div>
              </React.Fragment>
            )}
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:company')}
              {!isSecuritasPrivate && <span className='text-primary'>*</span>}
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t('formularFields:company')}
              value={customShipmentAddress.officeName}
              name='officeName'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress
                  .officeName
              }
              maxLength={
                customAddressSettings.companyMaxChars
                  ? customAddressSettings.companyMaxChars
                  : null
              }
            />
          </Form.Group>
          {!customAddressSettings.addressHideFirstname ? (
            <Form.Group className='mb-3'>
              <Form.Label>
                {t('formularFields:firstname')}
                <span className='text-primary'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                placeholder={t('formularFields:firstname')}
                value={customShipmentAddress.firstName}
                name='firstName'
                onChange={productsStore.handleChangeCustomShipmentAddress}
                isInvalid={
                  productsStore.requiredFieldsError.customShipmentAddress
                    .firstName
                }
                maxLength={
                  customAddressSettings.nameMaxChars
                    ? customAddressSettings.nameMaxChars
                    : null
                }
              />
            </Form.Group>
          ) : null}
          {!customAddressSettings.addressHideLastname ? (
            <Form.Group className='mb-3'>
              <Form.Label>
                {t('formularFields:lastname')}
                <span className='text-primary'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                placeholder={t('formularFields:lastname')}
                value={customShipmentAddress.lastName}
                name='lastName'
                onChange={productsStore.handleChangeCustomShipmentAddress}
                isInvalid={
                  productsStore.requiredFieldsError.customShipmentAddress
                    .lastName
                }
                maxLength={
                  customAddressSettings.nameMaxChars
                    ? customAddressSettings.nameMaxChars
                    : null
                }
              />
            </Form.Group>
          ) : null}
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:street')}
              <span className='text-primary'>*</span>
            </Form.Label>
            {/* <Form.Row className='mb-2'> */}
            <Form.Control
              type='text'
              placeholder={t('formularFields:street')}
              value={customShipmentAddress.street}
              name='street'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress.street
              }
              maxLength={
                customAddressSettings.streetMaxChars
                  ? customAddressSettings.streetMaxChars
                  : null
              }
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:housenumber')}
              <span className='text-primary'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t('formularFields:housenumber')}
              value={customShipmentAddress.streetNr}
              name='streetNr'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress.streetNr
              }
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:additionalAddress')}
              <span className='text-primary'></span>
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t('formularFields:additionalAddress')}
              value={customShipmentAddress.careOf}
              name='careOf'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              maxLength={
                customAddressSettings['c/oMaxChars']
                  ? customAddressSettings['c/oMaxChars']
                  : null
              }
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:zip')} <span className='text-primary'>*</span>
            </Form.Label>
            <ZipSuggestion
              type={
                customAddressSettings.zipcodeLimitNumericValues
                  ? 'number'
                  : 'text'
              }
              placeholder={t('formularFields:zip')}
              value={customShipmentAddress.zipCode}
              name='zipCode'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress.zipCode
              }
              onChangeSuggestion={(obj) =>
                productsStore.handleChangeCustomShipmentAddress(
                  getFakeEvent('city', obj.cityName, 'text')
                )
              }
              countryIsoCode={customShipmentAddress.countryIsoCode || 'de'}
              maxLength={
                customAddressSettings.zipcodeMaxChars
                  ? customAddressSettings.zipcodeMaxChars
                  : null
              }
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>
              {t('formularFields:city')} <span className='text-primary'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              placeholder={t('formularFields:city')}
              value={customShipmentAddress.city}
              name='city'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress.city
              }
              maxLength={
                customAddressSettings.cityMaxChars
                  ? customAddressSettings.cityMaxChars
                  : null
              }
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>{t('formularFields:country')}</Form.Label>
            <ArraySelect
              placeholder={t('formularFields:country')}
              value={customShipmentAddress.countryIsoCode}
              name='countryIsoCode'
              options={checkoutStore.shippingCountries}
              onChange={productsStore.handleChangeCustomShipmentAddress}
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress
                  .countryIsoCode
              }
              otherValueKey='isoCode'
              returnEvent
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Check
              label={t('confirmAddress:updateAddress')}
              name='updateUserAddress'
              onChange={productsStore.handleChangeCustomShipmentAddress}
              checked={customShipmentAddress.updateUserAddress}
              id='updateUserAddress'
              isInvalid={
                productsStore.requiredFieldsError.customShipmentAddress
                  .updateUserAddress
              }
            />
          </Form.Group>
        </Form>
      )
    }

    return (
      <React.Fragment>
        <StyledCustomAddressCard>
          <StyledCard
            withCheckbox={true}
            active={productsStore.customShipmentAddressSelected}
            onClick={() => handleCustomShipmentAddressCardClick()}
          >
            <StyledCard.Body className='mt-3'>
              <StyledCard.Body.Title>
                <StyledTitle>
                  <small className='text-primary text-uppercase fw-bold'>
                    {t('confirmAddress:customShippingAddress')}
                  </small>
                </StyledTitle>
              </StyledCard.Body.Title>
            </StyledCard.Body>
          </StyledCard>
        </StyledCustomAddressCard>
        {productsStore.customShipmentAddressSelected ? (
          <Col className='mt-3'>
            {mandate === 'cws' ? (
              <StyledCWSForm>{renderShipmentAddressForm()}</StyledCWSForm>
            ) : (
              renderShipmentAddressForm()
            )}
          </Col>
        ) : (
          ''
        )}
      </React.Fragment>
    )
  })
)

export default withTranslation()(CustomShipmentAddress)
